import { captureException } from '@sentry/browser';
import { ZodError } from 'zod';

import { apiClient } from '@f/api-client';

import { createAction } from '../actions';
import { getAuthenticatedUser } from '../authentication/authentication.selectors';
import { localStorageClient } from '../storage/wrapper';

export const REPORT_FIND_START = 'REPORT_FIND_START';
export const REPORT_FIND_FULFILL = 'REPORT_FIND_FULFILL';
export const REPORT_FIND_REJECT = 'REPORT_FIND_REJECT';

export const REPORT_GET_START = 'REPORT_GET_START';
export const REPORT_GET_FULFILL = 'REPORT_GET_FULFILL';
export const REPORT_GET_REJECT = 'REPORT_GET_REJECT';

export const REPORT_ITEMS_NUMBER_GET_START = 'REPORT_ITEMS_NUMBER_GET_START';
export const REPORT_ITEMS_NUMBER_GET_FULFILL = 'REPORT_ITEMS_NUMBER_GET_FULFILL';
export const REPORT_ITEMS_NUMBER_GET_REJECT = 'REPORT_ITEMS_NUMBER_GET_REJECT';

export const REPORT_CREATE_START = 'REPORT_CREATE_START';
export const REPORT_CREATE_FULFILL = 'REPORT_CREATE_FULFILL';
export const REPORT_CREATE_REJECT = 'REPORT_CREATE_REJECT';

export const REPORT_PATCH_START = 'REPORT_PATCH_START';
export const REPORT_PATCH_FULFILL = 'REPORT_PATCH_FULFILL';
export const REPORT_PATCH_REJECT = 'REPORT_PATCH_REJECT';

export const REPORT_REMOVE_START = 'REPORT_REMOVE_START';
export const REPORT_REMOVE_FULFILL = 'REPORT_REMOVE_FULFILL';
export const REPORT_REMOVE_REJECT = 'REPORT_REMOVE_REJECT';

export const REPORT_ITEM_PATCH_START = 'REPORT_ITEM_PATCH_START';
export const REPORT_ITEM_PATCH_FULFILL = 'REPORT_ITEM_PATCH_FULFILL';
export const REPORT_ITEM_PATCH_REJECT = 'REPORT_ITEM_PATCH_REJECT';

export const REPORT_ITEM_REMOVE_START = 'REPORT_ITEM_REMOVE_START';
export const REPORT_ITEM_REMOVE_FULFILL = 'REPORT_ITEM_REMOVE_FULFILL';
export const REPORT_ITEM_REMOVE_REJECT = 'REPORT_ITEM_REMOVE_REJECT';

export const REPORT_DESIGNING_MODE_START = 'REPORT_DESIGNING_MODE_START';
export const REPORT_DESIGNING_MODE_FINISH = 'REPORT_DESIGNING_MODE_FINISH';

const getEditingReportIdKey = userId => `${userId}-editingReportId`;

export function findReports(query = {}) {
  return async dispatch => {
    dispatch(createAction(REPORT_FIND_START, { query }));

    try {
      const { status, body } = await apiClient.report.fetchReports({ query });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_FIND_FULFILL, { result: body }));
      return {
        total: body.pagination.total,
        limit: query.limit,
        skip: query.skip,
        ids: body.data.map(item => item.id),
      };
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_FIND_REJECT, error));
      return error;
    }
  };
}

export function getReport(id) {
  return async dispatch => {
    dispatch(createAction(REPORT_GET_START, { id }));

    try {
      const { status, body } = await apiClient.report.fetchReport({ params: { id } });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_GET_FULFILL, { id, result: body }));
      return { id, result: body };
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_GET_REJECT, error));
      return error;
    }
  };
}

export function getReportItemsNumber(id) {
  return async dispatch => {
    dispatch(createAction(REPORT_ITEMS_NUMBER_GET_START, { id }));

    try {
      const { status, body } = await apiClient.report.fetchReportItemsNumber({ params: { id } });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_ITEMS_NUMBER_GET_FULFILL, { id, result: body }));
      return body;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_ITEMS_NUMBER_GET_REJECT, error));
      return error;
    }
  };
}

export function createReport({ shouldEnableEditingMode = true, data = {} } = {}) {
  return async (dispatch, getState) => {
    dispatch(createAction(REPORT_CREATE_START));

    try {
      const { status, body } = await apiClient.report.createReport({
        body: data,
      });

      if (status !== 201) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_CREATE_FULFILL, { result: body }));

      if (shouldEnableEditingMode) {
        enableReportDesigningMode(body.id)(dispatch, getState);
      }
      return body.id;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_CREATE_REJECT, error));
      return error;
    }
  };
}

export function patchReport(id, data = {}) {
  return async dispatch => {
    dispatch(createAction(REPORT_PATCH_START, { id, data }));

    try {
      const { status, body } = await apiClient.report.updateReport({ params: { id }, body: data });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_PATCH_FULFILL, { id, result: body }));
      return body.id;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_PATCH_REJECT, error));
      return error;
    }
  };
}

export function removeReports(ids) {
  return async (dispatch, getState) => {
    const state = getState();
    const { designingId } = state.report;

    if (ids.includes(designingId)) {
      disableReportDesigningMode()(dispatch, getState);
    }

    const parentAction = createAction(REPORT_REMOVE_START);
    dispatch(parentAction);

    try {
      const { status, body } = await apiClient.report.removeReports({ body: ids });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_REMOVE_FULFILL, { result: body }, parentAction));
      return ids;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_REMOVE_REJECT, error, parentAction));
      return error;
    }
  };
}

export function patchReportItem(reportId, itemId, updatedItem = {}) {
  return async dispatch => {
    dispatch(createAction(REPORT_ITEM_PATCH_START, { id: reportId, data: updatedItem }));

    try {
      const { status, body } = await apiClient.report.updateReportItem({
        params: { reportId, itemId },
        body: updatedItem,
      });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_ITEM_PATCH_FULFILL, { result: body }));
      return itemId;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_ITEM_PATCH_REJECT, error));
      return error;
    }
  };
}

export function removeReportItems(reportId, itemIds) {
  return async dispatch => {
    const parentAction = createAction(REPORT_ITEM_REMOVE_START, { id: reportId, data: itemIds }); // change data to query
    dispatch(parentAction);

    try {
      const { status, body } = await apiClient.report.removeReportItems({ params: { reportId }, body: itemIds });

      if (status !== 200) {
        throw new Error(body);
      }

      dispatch(createAction(REPORT_ITEM_REMOVE_FULFILL, { result: body }, parentAction));
      return itemIds;
    } catch (error) {
      if (error instanceof ZodError) {
        captureException(error);
      }
      dispatch(createAction(REPORT_ITEM_REMOVE_REJECT, error, parentAction));
      return error;
    }
  };
}

export function initReportDesigningMode() {
  return async (dispatch, getState) => {
    const user = getAuthenticatedUser(getState());

    if (!user) {
      return;
    }

    const key = getEditingReportIdKey(user.id);
    const reportId = localStorageClient.getItem(key);

    if (reportId) {
      dispatch(createAction(REPORT_DESIGNING_MODE_START, { id: reportId }));
    }
  };
}

export function enableReportDesigningMode(reportId) {
  return async (dispatch, getState) => {
    const user = getAuthenticatedUser(getState());

    if (!user) {
      return;
    }

    const key = getEditingReportIdKey(user.id);
    localStorageClient.setItem(key, reportId);
    dispatch(createAction(REPORT_DESIGNING_MODE_START, { id: reportId }));
  };
}

export function disableReportDesigningMode() {
  return async (dispatch, getState) => {
    const user = getAuthenticatedUser(getState());

    if (!user) {
      return;
    }

    const key = getEditingReportIdKey(user.id);
    localStorageClient.removeItem(key);
    dispatch(createAction(REPORT_DESIGNING_MODE_FINISH));
  };
}
